import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, doc, updateDoc, Timestamp } from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faLocationDot, faStairs, faCreditCard, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import "../styles/pages/admin.scss";

const ACCOUNTS = {
  admin: { password: "adminidzuo123", role: "admin" },
  kuhinja: { password: "adminidzuo123", role: "kuhinja" },
  dostava: { password: "adminidzuo123", role: "dostava" },
};

const CountdownTimer = ({ acceptedAt, deliveryTime }) => {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!acceptedAt || !deliveryTime) return;
    const deadline = acceptedAt.toDate().getTime() + deliveryTime * 60 * 1000;
    const tick = () => setRemaining(deadline - Date.now());
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [acceptedAt, deliveryTime]);

  if (remaining === null) return null;

  if (remaining <= 0) {
    return <div className="order-timer expired"><FontAwesomeIcon icon={faTriangleExclamation} /> Каснимо</div>;
  }

  const totalSeconds = Math.floor(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const isUrgent = remaining < 5 * 60 * 1000;

  return (
    <div className={`order-timer${isUrgent ? " urgent" : ""}`}>
      {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
    </div>
  );
};

const OrderCard = ({ order, showImages = true, actions }) => (
  <div className={`order-card ${order.status}`}>
    <div className="card-content">
      <div className="order-meta">
        {order.timestamp && (
          <span className="order-timestamp">
            {order.timestamp.toDate().toLocaleString("sr-RS", {
              day: "2-digit", month: "2-digit", year: "numeric",
              hour: "2-digit", minute: "2-digit"
            })}
          </span>
        )}
        {order.deliveryTime && (
          <span className="delivery-time">⏱ {order.deliveryTime} мин</span>
        )}
      </div>
      <h5>{order.name}</h5>
      <ul>
        <li>
          <span><FontAwesomeIcon icon={faPhone} /></span>
          <span>
            {actions?.callPhone ? (
              <a href={`tel:${order.phone}`}>{order.phone}</a>
            ) : (
              order.phone
            )}
          </span>
        </li>
        <li>
          <span><FontAwesomeIcon icon={faLocationDot} /></span>
          <span>{order.street}</span>
        </li>
        {order.floor && (
          <li>
            <span><FontAwesomeIcon icon={faStairs} /></span>
            <span>{order.floor}</span>
          </li>
        )}
        <li>
          <span><FontAwesomeIcon icon={faCreditCard} /></span>
          <span>{order.payment}</span>
        </li>
      </ul>
      {order.notice && (
        <span className="order-notice">{order.notice}</span>
      )}
      <div className="items-list">
        {order.cartItems && order.cartItems.length > 0 ? (
          order.cartItems.map((item, idx) => (
            <div key={idx} className="item">
              {showImages && item.image && (
                <img src={item.image} alt={item.name} className="item-image" />
              )}
              <div className="item-info">
                <p>
                  {item.qty}x {item.name}{" "}
                  {item.subtitle && `(${item.subtitle})`}
                </p>
                <p className="item-price">{item.price * item.qty} рсд</p>
              </div>
            </div>
          ))
        ) : (
          <p>{order.products}</p>
        )}
      </div>
      <p className="order-price">
        <span>Укупно:</span> <span>{order.totalPrice}</span>
      </p>
      {order.acceptedAt && order.deliveryTime && (order.status === "accepted" || order.status === "na_dostavi") && (
        <CountdownTimer acceptedAt={order.acceptedAt} deliveryTime={order.deliveryTime} />
      )}
      {order.wasLate && order.status === "completed" && (
        <p className="was-late">Припрема је каснила</p>
      )}
      {order.rejectReason && (
        <p className="reject-reason">{order.rejectReason}</p>
      )}
      {actions?.buttons && (
        <div className="button-group">{actions.buttons}</div>
      )}
    </div>
  </div>
);

const Admin = () => {
  const [orders, setOrders] = useState([]);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem("adminRole"));
  const [role, setRole] = useState(() => localStorage.getItem("adminRole"));
  const [loginError, setLoginError] = useState("");
  const [archiveFilter, setArchiveFilter] = useState("all");
  const [rejectDialog, setRejectDialog] = useState(null);
  const [acceptDialog, setAcceptDialog] = useState(null);
  const soundIntervalRef = useRef(null);

  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.frequency.value = 880;
      oscillator.type = "sine";
      gainNode.gain.setValueAtTime(1.0, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.6);
    } catch (e) {}
  };

  const REJECT_REASONS = [
    "Тренутно немамо све потребне намирнице",
    "Ресторан је тренутно затворен",
    "Превелика гужва, не можемо да прихватимо нове поруџбине",
    "Адреса испоруке је ван нашег подручја",
    "Поруџбина није потпуна или је неважећа",
  ];

  useEffect(() => {
    if (!isLoggedIn) return;
    const unsubscribe = onSnapshot(collection(db, "orders"), (snapshot) => {
      const allOrders = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setOrders(
        allOrders.sort(
          (a, b) =>
            (b.timestamp?.toDate?.() || 0) - (a.timestamp?.toDate?.() || 0),
        ),
      );
    });
    return unsubscribe;
  }, [isLoggedIn]);

  useEffect(() => {
    const pendingCount = orders.filter((o) => o.status === "pending").length;
    if (pendingCount > 0) {
      if (!soundIntervalRef.current) {
        playBeep();
        soundIntervalRef.current = setInterval(playBeep, 500);
      }
    } else {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
    }
  }, [orders]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => clearInterval(soundIntervalRef.current);
  }, []);

  const acceptOrder = async (orderId, deliveryTime) => {
    try {
      await updateDoc(doc(db, "orders", orderId), { status: "accepted", deliveryTime, acceptedAt: Timestamp.fromDate(new Date()) });
      setAcceptDialog(null);
    } catch (error) {
      console.error("Error accepting order: ", error);
    }
  };

  const readyOrder = async (orderId) => {
    try {
      await updateDoc(doc(db, "orders", orderId), { status: "na_dostavi" });
    } catch (error) {
      console.error("Error marking order as ready: ", error);
    }
  };

  const rejectOrder = async (orderId, reason) => {
    try {
      await updateDoc(doc(db, "orders", orderId), {
        status: "rejected",
        rejectReason: reason,
      });
      setRejectDialog(null);
    } catch (error) {
      console.error("Error rejecting order: ", error);
    }
  };

  const completeOrder = async (order) => {
    try {
      const wasLate = order.acceptedAt && order.deliveryTime
        ? Date.now() > order.acceptedAt.toDate().getTime() + order.deliveryTime * 60 * 1000
        : false;
      await updateDoc(doc(db, "orders", order.id), { status: "completed", ...(wasLate && { wasLate: true }) });
    } catch (error) {
      console.error("Error completing order: ", error);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    const account = ACCOUNTS[username];
    if (account && account.password === password) {
      localStorage.setItem("adminRole", account.role);
      setIsLoggedIn(true);
      setRole(account.role);
      setLoginError("");
    } else {
      setLoginError("Неисправно корисничко име или лозинка.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminRole");
    setIsLoggedIn(false);
    setRole(null);
    setUsername("");
    setPassword("");
  };

  if (!isLoggedIn) {
    return (
      <div className="content row v-center h-center">
        <div className="login">
          <h2>ПРИЈАВА</h2>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Корисничко ime</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder=""
                required
              />
            </div>
            <div className="form-group">
              <label>Лозинка</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder=""
                required
              />
            </div>
            {loginError && <p className="login-error">{loginError}</p>}
            <button type="submit" className="button">
              Пријави се
            </button>
          </form>
        </div>
      </div>
    );
  }

  // DOSTAVA VIEW
  if (role === "dostava") {
    const inProgressOrders = orders.filter((o) => o.status === "accepted");
    const deliveryOrders = orders.filter((o) => o.status === "na_dostavi");
    const completedOrders = orders.filter((o) => o.status === "completed");
    return (
      <>
        <div className="header admin">
          <div className="row v-center space-between">
            <h1>Достава</h1>
            <button onClick={handleLogout} className="button logout">
              Одјави се
            </button>
          </div>
        </div>
        <div className="content admin">
          <div className="content-in row v-start h-start cols-3">
            <div className="admin-column">
              <h4>У припреми</h4>
              {inProgressOrders.length === 0 ? (
                <p className="empty-message">Нема поруџбина у припреми</p>
              ) : (
                inProgressOrders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    showImages={false}
                    actions={{ callPhone: true }}
                  />
                ))
              )}
            </div>
            <div className="admin-column">
              <h4>На достави</h4>
              {deliveryOrders.length === 0 ? (
                <p className="empty-message">Нема поруџбина на достави</p>
              ) : (
                deliveryOrders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    showImages={false}
                    actions={{
                      callPhone: true,
                      buttons: (
                        <button
                          className="btn-complete"
                          onClick={() => completeOrder(order)}
                        >
                          Достављено
                        </button>
                      ),
                    }}
                  />
                ))
              )}
            </div>
            <div className="admin-column">
              <h4>Завршене</h4>
              {completedOrders.length === 0 ? (
                <p className="empty-message">Нема завршених поруџбина</p>
              ) : (
                completedOrders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    showImages={false}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // KUHINJA VIEW
  if (role === "kuhinja") {
    return (
      <>
        <div className="header admin">
          <div className="row v-center space-between">
            <h1>Кухиња</h1>
            <button onClick={handleLogout} className="button logout">
              Одјави се
            </button>
          </div>
        </div>
        <div className="content admin">
          <div className="content-in row v-start h-start cols-3">
            <div className="admin-column">
              <h4>Нове поруџбине</h4>
              {orders.filter((o) => o.status === "pending").length === 0 ? (
                <p className="empty-message">Нема нових поруџбина</p>
              ) : (
                orders
                  .filter((o) => o.status === "pending")
                  .map((order) => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      actions={{
                        buttons: (
                          <>
                            <button
                              className="btn-accept"
                              onClick={() => setAcceptDialog(order.id)}
                            >
                              Прихвати
                            </button>
                            <button
                              className="btn-reject"
                              onClick={() => setRejectDialog(order.id)}
                            >
                              Одбиј
                            </button>
                          </>
                        ),
                      }}
                    />
                  ))
              )}
            </div>
            <div className="admin-column">
              <h4>У припреми</h4>
              {orders.filter((o) => o.status === "accepted").length === 0 ? (
                <p className="empty-message">Нема поруџбина у припреми</p>
              ) : (
                orders
                  .filter((o) => o.status === "accepted")
                  .map((order) => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      actions={{
                        buttons: (
                          <button
                            className="btn-complete"
                            onClick={() => readyOrder(order.id)}
                          >
                            Припремљено
                          </button>
                        ),
                      }}
                    />
                  ))
              )}
            </div>
            <div className="admin-column">
              <h4>На достави</h4>
              {orders.filter((o) => o.status === "na_dostavi").length === 0 ? (
                <p className="empty-message">Нема поруџбина на достави</p>
              ) : (
                orders
                  .filter((o) => o.status === "na_dostavi")
                  .map((order) => <OrderCard key={order.id} order={order} />)
              )}
            </div>
          </div>
        </div>
        {acceptDialog && (
          <div className="reject-overlay" onClick={() => setAcceptDialog(null)}>
            <div className="reject-dialog" onClick={(e) => e.stopPropagation()}>
              <h5>Вријеме испоруке</h5>
              <ul>
                {[30, 45, 60].map((min) => (
                  <li key={min} onClick={() => acceptOrder(acceptDialog, min)}>
                    {min} минута
                  </li>
                ))}
              </ul>
              <button className="btn-cancel" onClick={() => setAcceptDialog(null)}>
                Откажи
              </button>
            </div>
          </div>
        )}
        {rejectDialog && (
          <div className="reject-overlay" onClick={() => setRejectDialog(null)}>
            <div className="reject-dialog" onClick={(e) => e.stopPropagation()}>
              <h5>Разлог одбијања</h5>
              <ul>
                {REJECT_REASONS.map((reason, idx) => (
                  <li
                    key={idx}
                    onClick={() => rejectOrder(rejectDialog, reason)}
                  >
                    {reason}
                  </li>
                ))}
              </ul>
              <button
                className="btn-cancel"
                onClick={() => setRejectDialog(null)}
              >
                Откажи
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  // ADMIN VIEW
  return (
    <>
      <div className="header admin">
        <div className="row v-center space-between">
          <h1>Поруџбине</h1>
          <button onClick={handleLogout} className="button logout">
            Одјави се
          </button>
        </div>
      </div>

      <div className="content admin">
        <div className="content-in row v-start h-start">
          <div className="admin-column">
            <h4>Нове поруџбине</h4>
            {orders.filter((o) => o.status === "pending").length === 0 ? (
              <p className="empty-message">Нема нових поруџбина</p>
            ) : (
              orders
                .filter((o) => o.status === "pending")
                .map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    actions={{
                      buttons: (
                        <>
                          <button
                            className="btn-accept"
                            onClick={() => setAcceptDialog(order.id)}
                          >
                            Прихвати
                          </button>
                          <button
                            className="btn-reject"
                            onClick={() => setRejectDialog(order.id)}
                          >
                            Одбиј
                          </button>
                        </>
                      ),
                    }}
                  />
                ))
            )}
          </div>

          <div className="admin-column">
            <h4>У припреми</h4>
            {orders.filter((o) => o.status === "accepted").length === 0 ? (
              <p className="empty-message">Нема поруџбина у припреми</p>
            ) : (
              orders
                .filter((o) => o.status === "accepted")
                .map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    actions={{
                      buttons: (
                        <button
                          className="btn-complete"
                          onClick={() => readyOrder(order.id)}
                        >
                          Припремљено
                        </button>
                      ),
                    }}
                  />
                ))
            )}
          </div>

          <div className="admin-column">
            <h4>На достави</h4>
            {orders.filter((o) => o.status === "na_dostavi").length === 0 ? (
              <p className="empty-message">Нема поруџбина на достави</p>
            ) : (
              orders
                .filter((o) => o.status === "na_dostavi")
                .map((order) => <OrderCard key={order.id} order={order} />)
            )}
          </div>

          <div className="admin-column">
            <div className="column-header">
              <h4>Завршене</h4>
              <div className="archive-legend">
                <span
                  className={`legend-item completed ${archiveFilter === "completed" ? "active" : ""}`}
                  onClick={() =>
                    setArchiveFilter(
                      archiveFilter === "completed" ? "all" : "completed",
                    )
                  }
                >
                  Завршене
                </span>
                <span
                  className={`legend-item rejected ${archiveFilter === "rejected" ? "active" : ""}`}
                  onClick={() =>
                    setArchiveFilter(
                      archiveFilter === "rejected" ? "all" : "rejected",
                    )
                  }
                >
                  Одбијене
                </span>
              </div>
            </div>
            {orders.filter((o) =>
              archiveFilter === "all"
                ? o.status === "completed" || o.status === "rejected"
                : o.status === archiveFilter,
            ).length === 0 ? (
              <p className="empty-message">
                Нема завршених или одбијених поруџбина
              </p>
            ) : (
              orders
                .filter((o) =>
                  archiveFilter === "all"
                    ? o.status === "completed" || o.status === "rejected"
                    : o.status === archiveFilter,
                )
                .map((order) => <OrderCard key={order.id} order={order} />)
            )}
          </div>
        </div>
      </div>

      {acceptDialog && (
        <div className="reject-overlay" onClick={() => setAcceptDialog(null)}>
          <div className="reject-dialog" onClick={(e) => e.stopPropagation()}>
            <h5>Вријеме испоруке</h5>
            <ul>
              {[{ label: "5 секунди", value: 5 / 60 }, { label: "5 минута", value: 5 }].map((opt) => (
                <li key={opt.label} onClick={() => acceptOrder(acceptDialog, opt.value)}>
                  {opt.label}
                </li>
              ))}
            </ul>
            <button className="btn-cancel" onClick={() => setAcceptDialog(null)}>
              Откажи
            </button>
          </div>
        </div>
      )}
      {rejectDialog && (
        <div className="reject-overlay" onClick={() => setRejectDialog(null)}>
          <div className="reject-dialog" onClick={(e) => e.stopPropagation()}>
            <h5>Разлог одбијања</h5>
            <ul>
              {REJECT_REASONS.map((reason, idx) => (
                <li key={idx} onClick={() => rejectOrder(rejectDialog, reason)}>
                  {reason}
                </li>
              ))}
            </ul>
            <button
              className="btn-cancel"
              onClick={() => setRejectDialog(null)}
            >
              Откажи
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Admin;
